import React, { useCallback, useContext, useEffect, useState } from 'react'
import Layout from './Layout'
import { MessageContext } from './Contexts'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useParams, Link, useNavigate } from 'react-router'
import { EditUserType, InviteUsers, ListUsers, ListUserTypes, TypeSettingsUser, QAListBranchRights } from 'modules/Rest'
import AddIcon from '@mui/icons-material/AddCircleOutline'
import GridCard from './GridCard'
import CardHeader from './CardHeader'
import GroupManager from './GroupManager'
import UsersTable from './UsersTable'
import FolderTree from './FolderTree'
import GridContainer from './GridContainer'
import AButton from './AButton'
import { CreateLinkDialog, ViewLinkDialog } from './InviteLinkDialog'
import { setModes } from 'modules/Utils'
import { AddUsersDialog } from './AddUsersDialog'
import { LoadingDotsIcon, LinkPlusIcon } from './Icons'
import { sortBy } from 'lodash'
import { useSelectedDataRoom } from 'hooks/useSelectedDataRoom'

const useStyles = makeStyles(theme => ({
  disclaimerField: {
    width: '100%',
  },
  tableFooter: {
    paddingTop: 5,
    fontStyle: 'italic',
    '& a, a:visited': {
      color: 'rgb(26,107,173)',
    },
  },
}))

export default function UserRights() {
  const classes = useStyles()
  const { dataRoom } = useSelectedDataRoom()
  const { messageDispatch } = useContext(MessageContext)
  const navigate = useNavigate()

  const { userTypeKey, dataRoomKey = '' } = useParams()
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [send_file_update_notification, setSendFileUpdateNotification] = useState(false)
  const [allow_dr_download, setAllowDRDownload] = useState(false)
  const [allow_index, setAllowIndex] = useState(false)
  const [f_disclaimer, setFDisclaimer] = useState('')
  const [a_disclaimer, setADisclaimer] = useState('')
  const [twoFactorAllowed, setTwoFactorAllowed] = useState('')
  const [cachedSettings, setCachedSettings] = useState(null)
  const [linkAccess, setLinkAccess] = useState(false)
  const [inviteLink, setInviteLink] = useState('')
  const [branchRights, setBranchRights] = useState([])
  const [branchRightsLoading, setBranchRightsLoading] = useState(false)

  const [modeDisclaimers, setModeDisclaimers] = useState('')
  const [modeAddUsers, setModeAddUsers] = useState('')
  const [modeAddLink, setModeAddLink] = useState('')
  const [modeSettings, setModeSettings] = useState('')
  const [modeViewLinkDialog, setModeViewLinkDialog] = useState('')

  const getUserTypes = useCallback(async () => {
    try {
      const { data: { success, info, user_types } } = await ListUserTypes({data_room: dataRoomKey})
      if (success) {
        return user_types
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
      return []
    }
  }, [dataRoomKey, messageDispatch])

  const getUsers = useCallback(async () => {
    try {
      setUsersLoading(true)
      const { data: { success, info, users } } = await ListUsers({user_type: userTypeKey})
      if (success) {
        setUsers(sortBy(users, (user_i) => (user_i.user_name.toUpperCase())))
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
    setUsersLoading(false)
  }, [userTypeKey, messageDispatch])

  const getQABranchRights = useCallback(async params => {
    try {
      setBranchRightsLoading(true)
      const { data: { success, info, q_branches } } = await QAListBranchRights(params)
      if (success) {
        setBranchRights(q_branches)
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
    setBranchRightsLoading(false)
  }, [messageDispatch])

  const saveSettings = async (e, setMode) => {
    try {
      setMode('pending')
      const { data: { success, info } } = await TypeSettingsUser({
        user_type: userTypeKey,
        send_file_update_notification,
        allow_index,
        allow_dr_download,
      })
      if (success) {
        setModes(setMode, ['completing', 1500, ''])
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setMode('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const saveDisclaimers = async (e, setMode) => {
    try {
      setMode('pending')
      const { data: { success, info } } = await TypeSettingsUser({
        user_type: userTypeKey,
        a_disclaimer,
        f_disclaimer,
      })
      if (success) {
        setModes(setMode, ['completing', 1500, ''])
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setMode('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const setDisclaimers = useCallback((data) => {
      setADisclaimer(data.a_disclaimer)
      setFDisclaimer(data.f_disclaimer)
  }, [setADisclaimer, setFDisclaimer])

  const setOtherSettings = useCallback((data) => {
    setSendFileUpdateNotification(data.send_file_update_notification)
    setAllowDRDownload(data.allow_dr_download)
    setAllowIndex(data.allow_index)
    setLinkAccess(data.link_access)
  }, [setSendFileUpdateNotification, setAllowDRDownload, setAllowIndex, setLinkAccess])

  const getUserSettings = useCallback(async params => {
    try {
      setSettingsLoading(true)
      const { data } = await TypeSettingsUser(params)
      if (data && data.success) {
        setCachedSettings(data)
        setOtherSettings(data)
        setDisclaimers(data)
        setTwoFactorAllowed(data.two_factor_allowed)
      } else {
        throw new Error(data && data.info ? data.info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
    setSettingsLoading(false)
  }, [setCachedSettings, setOtherSettings, setDisclaimers, setTwoFactorAllowed, messageDispatch])

  useEffect(() => {
    if (userTypeKey) {
      getUsers()
      getUserSettings({user_type: userTypeKey})
      getQABranchRights({user_type: userTypeKey})
    }
  }, [userTypeKey, getUsers, getUserSettings, getQABranchRights])

  const switchGroup = useCallback((newGroupId) => {
    navigate(`/user-rights/${dataRoomKey}/${newGroupId}`);
  }, [navigate, dataRoomKey]);

  const viewLink = ({ link }) => {
    setInviteLink(link)
    setModeViewLinkDialog('editing')
  }

  return (
    (<Layout>
      {!dataRoomKey  && <Box>
        <Typography variant='h5'>Please select a data room</Typography>
      </Box>}
      {dataRoom && <GridContainer>
        <GridCard sm={12} uniformPadding={true}>
          <GroupManager
            getGroups={getUserTypes}
            editGroup={EditUserType}
            switchGroup={switchGroup}
            selectedGroupKey={userTypeKey}
            groupName='User Group'
            btnGroupName='Group'
            groupAttr='user_type'
            groupDisplayAttr='user_type_name'
          />
        </GridCard>

        {userTypeKey && <GridCard sm={12}>
          <CardHeader
            title='Users'
            headingElement='h5'
            buttons={[
              <AButton startIcon={<AddIcon/>} onClick={() => setModeAddUsers('editing')}>Add users</AButton>,
              linkAccess ? <AButton startIcon={<LinkPlusIcon/>} onClick={() => setModeAddLink('editing')}>Create generic link</AButton> : null,
            ]}
          />
          {usersLoading && <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>}
          {!usersLoading && <UsersTable users={users} setUsers={setUsers} userTypeKey={userTypeKey} twoFactor={twoFactorAllowed} linkAccess={linkAccess} viewLink={viewLink}/>}

          <CreateLinkDialog
            mode={modeAddLink}
            setMode={setModeAddLink}
            userTypeKey={userTypeKey}
            getUsers={getUsers}
            viewLink={viewLink}
          />

          <AddUsersDialog
            mode={modeAddUsers}
            setMode={setModeAddUsers}
            callback={getUsers}
            inviteFn={InviteUsers}
            params={{user_type: userTypeKey}}
          />
        </GridCard>}

        {userTypeKey && <GridCard sm={12} md={6}>
          <CardHeader
            mode={modeSettings}
            setMode={setModeSettings}
            action={saveSettings}
            cancel={(e, setMode) => {
              setOtherSettings(cachedSettings)
              setMode('')
            }}
            title='Settings'
            headingElement='h5'
          />
          {settingsLoading && <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>}
          {!settingsLoading && <>
            <FormControlLabel
              disabled={modeSettings !== 'editing'}
              control={
                <Checkbox
                  checked={send_file_update_notification}
                  onChange={({ target: { checked } }) => setSendFileUpdateNotification(checked)}
                />
              }
              label='Send email notifications when data room is updated'
            />
            {!dataRoom.light && <FormControlLabel
              disabled={modeSettings !== 'editing'}
              control={
                <Checkbox checked={allow_index} onChange={({ target: { checked } }) => setAllowIndex(checked)}/>
              }
              label='Enable users to download an index'
            />}
            <FormControlLabel
              disabled={modeSettings !== 'editing'}
              control={
                <Checkbox
                  checked={allow_dr_download}
                  onChange={({ target: { checked } }) => setAllowDRDownload(checked)}
                />
              }
              label='Enable users to download the whole data room'
            />
          </>}
        </GridCard>}

        {userTypeKey && <GridCard sm={12} md={6}>
          <CardHeader
            mode={modeDisclaimers}
            setMode={setModeDisclaimers}
            action={saveDisclaimers}
            cancel={(e, setMode) => {
              setDisclaimers(cachedSettings)
              setMode('')
            }}
            title='Disclaimers'
            headingElement='h5'
          />
          {settingsLoading && <LoadingDotsIcon size={6} vPadding={6} hPadding={6}/>}
          {!settingsLoading && <Grid container spacing={2}>
            <Grid item sm={12}>
              <TextField
                disabled={modeDisclaimers !== 'editing'}
                label='Shown during first login'
                multiline
                variant='outlined'
                value={f_disclaimer}
                onChange={({target: {value}}) => setFDisclaimer(value)}
                className={classes.disclaimerField}
                minRows={3}
                maxRows={6}
              />
            </Grid>
            <Grid item sm={12}>
              <TextField
                disabled={modeDisclaimers !== 'editing'}
                label='Shown during all (other) logins'
                multiline
                variant='outlined'
                value={a_disclaimer}
                onChange={({target: {value}}) => setADisclaimer(value)}
                className={classes.disclaimerField}
                minRows={3}
                maxRows={6}
              />
            </Grid>
          </Grid>}
        </GridCard>}

        {userTypeKey && <GridCard sm={12}>
          <Typography variant='h5'>
            Folder rights
          </Typography>
          <FolderTree dataRoom={dataRoom.data_room} userType={userTypeKey} lightDataRoom={dataRoom.light}/>
        </GridCard>}

        {!branchRightsLoading && userTypeKey && branchRights.length > 1 && <GridCard sm={12}>
          <CardHeader
            title="Q&A rights summary"
            headingElement="h5"
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Q&A branch</TableCell>
                  <TableCell>Visible to User Group</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branchRights.map(branch_i => <TableRow key={branch_i.q_branch}>
                  <TableCell>{branch_i.name}</TableCell>
                  <TableCell>{branch_i.q_read ? 'Yes' : 'No'}</TableCell>
                </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant='body2' className={classes.tableFooter}>Q&A rights can be edited on the <Link to={dataRoom && dataRoom.data_room ? `/qa/${dataRoom.data_room}` : '/qa/'}>Q&A page</Link>.</Typography>
        </GridCard>}
      </GridContainer>}
      <ViewLinkDialog
        mode={modeViewLinkDialog}
        setMode={setModeViewLinkDialog}
        link={inviteLink}
      />
    </Layout>)
  );
}
